import React, { useEffect, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  CreditCard,
  Calendar,
  XCircle,
  AlertCircle,
  Shield,
  MessageSquare
} from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const TermsAndConditionsCardLayout = () => {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-in-out'
    });
  }, []);

  const [activeSection, setActiveSection] = useState('payment');
  const [openFaqs, setOpenFaqs] = useState({});

  const toggleFaq = (id) => {
    setOpenFaqs((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const FaqItem = ({ id, question, answer }) => (
    <div className="border-b border-gray-200 last:border-0">
      <button
        className="w-full py-4 flex justify-between items-center text-left focus:outline-none transition-all duration-300"
        onClick={() => toggleFaq(id)}
      >
        <span className="font-medium text-gray-800 flex items-center">
          <AlertCircle size={18} className="text-amber-500 mr-2" />
          {question}
        </span>
        {openFaqs[id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {openFaqs[id] && (
        <div className="pb-4 text-gray-600 animate-fadeIn transition-opacity duration-300 ease-in-out">
          {answer}
        </div>
      )}
    </div>
  );

  const sections = [
    { id: 'payment', label: 'Payment', icon: <CreditCard size={20} /> },
    { id: 'reservation', label: 'Reservation', icon: <Calendar size={20} /> },
    { id: 'cancellation', label: 'Cancellation', icon: <XCircle size={20} /> },
    { id: 'rules', label: 'Rules', icon: <AlertCircle size={20} /> },
    { id: 'privacy', label: 'Privacy', icon: <Shield size={20} /> },
    { id: 'contact', label: 'Contact', icon: <MessageSquare size={20} /> }
  ];

  const SectionCard = ({ icon, title, children }) => (
    <div className="bg-white rounded-xl shadow-md overflow-hidden" data-aos="fade-up">
      <div className="p-6 bg-amber-700 text-white flex items-center rounded-t-xl">
        <div className="mr-3">{icon}</div>
        <h2 className="text-2xl font-semibold">{title}</h2>
      </div>
      <div className="p-6 prose max-w-none leading-relaxed tracking-wide text-gray-800">
        {children}
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-gray-50 to-amber-50 min-h-screen">
      {/* Header */}
      <div className="relative">
        {/* Background with dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-800"></div>

        {/* Subtle geometric pattern overlay */}
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px',
          }}
        ></div>

        {/* Main header content */}
        <header className="relative py-16 md:py-20" data-aos="fade-down">
          <div className="container mx-auto px-6">
            {/* Content layout */}
            <div className="flex flex-col md:flex-row items-center">
              {/* Main content area */}
              <div className="md:w-3/5 text-white md:pr-12 pt-20 pb-12">
                <div className="inline-flex items-center mb-3 px-3 py-1 bg-yellow-900 bg-opacity-50 border border-yellow-700 rounded text-yellow-400 text-xs font-medium uppercase tracking-wider">
                  <Shield size={14} className="mr-2" />
                  Legal Agreement
                </div>

                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                  Terms & Conditions
                </h1>

                <div className="h-1 w-16 bg-yellow-600 mb-6"></div>

                <p className="text-gray-300 md:text-lg max-w-2xl leading-relaxed">
                  Thank you for choosing The Gulmohar Grand . This document outlines the comprehensive terms and conditions governing your stay and use of our services. By making a reservation or using our facilities, you agree to these terms.
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Decorative bottom line */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="h-1 w-full bg-yellow-700"></div>
          <div className="h-px w-full bg-yellow-500 mt-1"></div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="lg:grid lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <nav className="lg:col-span-1 mb-8 lg:mb-0">
              <div className="bg-white rounded-xl shadow-md sticky top-8" data-aos="fade-right">
                <ul>
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center p-4 text-left transition-all duration-300 ease-in-out border-l-4 rounded-r-lg ${activeSection === section.id
                            ? 'border-amber-600 bg-amber-100 text-amber-800 shadow-sm'
                            : 'border-transparent text-gray-600 hover:bg-gray-100 hover:text-amber-700'
                          }`}
                      >
                        <span className="mr-3">{section.icon}</span>
                        <span className="font-medium">{section.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>

            {/* Main Content */}
            <div className="lg:col-span-3 space-y-10">
              {activeSection === 'payment' && (
                <SectionCard icon={<CreditCard />} title="Payment Policy">
                  <p>
                    To confirm your reservation, we require a deposit of 50% of the total stay amount at the time of booking.
                    The remaining balance must be paid upon check-in. We accept major credit cards, debit cards, and cash payments.
                  </p>
                  <p>
                    All quoted rates are subject to applicable taxes and service charges. Current tax rate is 18% GST. Additional services such as room service,
                    spa treatments, or special requests may incur extra charges.
                  </p>
                  <div className="mt-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Frequently Asked Questions</h3>
                    <div className="border rounded-lg divide-y px-4">
                      <FaqItem
                        id="payment1"
                        question="Are there any extra fees that I will have to pay on top of the listed price?"
                        answer="All room rates are subject to applicable taxes (currently 18% GST). Additional services such as room service, spa treatments, or special requests may incur extra charges. Resort fees of INR 1000 per night cover amenities including WiFi, pool access, fitness center, and daily newspaper."
                      />
                      <FaqItem
                        id="payment2"
                        question="Should I print a receipt to show the hotel reception?"
                        answer="While it's not mandatory to print your booking confirmation, we recommend having it accessible either in print or digital form during check-in for a smoother process."
                      />
                      <FaqItem
                        id="payment3"
                        question="How much does it cost to do a private company group?"
                        answer="Corporate group rates vary based on season, group size, and required services. We offer special packages for corporate events with discounted room rates for groups of 10 or more."
                      />
                    </div>
                  </div>
                </SectionCard>
              )}

              {activeSection === 'reservation' && (
                <SectionCard icon={<Calendar />} title="Reservation Policy">
                  <p>
                    Reservations can be made online through our official website, by phone, or via approved travel partners.
                    Check-in time is 12:00 PM and check-out time is 12:00 PM.
                  </p>
                  <p>
                    A valid government-issued photo ID is required at check-in. For international guests, a valid passport and visa (if applicable) are required.
                  </p>
                  <div className="mt-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Frequently Asked Questions</h3>
                    <div className="border rounded-lg divide-y px-4">
                      <FaqItem
                        id="reservation1"
                        question="What is your refund policy?"
                        answer="Cancellations made 7+ days before check-in receive a full refund minus a 5% fee. 3–7 days before check-in: 50% refund. Less than 3 days: non-refundable."
                      />
                      <FaqItem
                        id="reservation2"
                        question="Do you offer 30 days money back guarantee?"
                        answer="We do not offer a 30-day money-back guarantee as our services are date-specific."
                      />
                      <FaqItem
                        id="reservation3"
                        question="Which currency do you accept?"
                        answer="We accept INR. International cards are billed in INR. We also accept USD, EUR, and GBP at the reception."
                      />
                    </div>
                  </div>
                </SectionCard>
              )}

              {activeSection === 'cancellation' && (
                <SectionCard icon={<XCircle />} title="Cancellation Policy">
                  <p>
                    We understand that plans change. To modify or cancel a reservation, please contact our reservations team as early as possible.
                    Cancellations made 7+ days before check-in will be refunded minus a 5% fee.
                  </p>
                  <p>
                    Within 7 days, deposit is forfeited. No-shows will be charged the full reservation amount.
                  </p>
                </SectionCard>
              )}

              {activeSection === 'rules' && (
                <SectionCard icon={<AlertCircle />} title="General Rules & Regulations">
                  <ul className="list-disc list-inside space-y-1">
                    <li>Valid ID required at check-in.</li>
                    <li>Smoking only in designated areas.</li>
                    <li>Pets allowed with prior approval.</li>
                    <li>Quiet hours: 10 PM – 7 AM.</li>
                    <li>Damage to property will be charged.</li>
                    <li>Hotel not responsible for lost/stolen items.</li>
                    <li>No outside food in public areas.</li>
                    <li>Pool hours: 7 AM – 9 PM. Children must be supervised.</li>
                  </ul>
                </SectionCard>
              )}

              {activeSection === 'privacy' && (
                <SectionCard icon={<Shield />} title="Privacy Policy">
                  <p>
                    We collect personal info for reservations, communication, and service improvements. Your data is safe with us and never shared unless required by law.
                  </p>
                  <h3>Information We Collect</h3>
                  <p>Name, contact info, payment details, stay preferences.</p>
                  <h3>Use of Information</h3>
                  <ul className="list-disc list-inside">
                    <li>Reservation and check-in</li>
                    <li>Payment processing</li>
                    <li>Communication</li>
                    <li>Improving services</li>
                    <li>Legal compliance</li>
                  </ul>
                </SectionCard>
              )}

              {activeSection === 'contact' && (
                <SectionCard icon={<MessageSquare />} title="Contact Information">
                  <p>
                    If you have any questions or concerns, feel free to contact us anytime.
                  </p>
                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h4 className="text-lg font-medium text-amber-800 mb-2">Reservations</h4>
                      <p>Email: info@gulmohargrandhotel.com</p>
                      <p>Phone: +91 (0) 6230562306</p>
                      <p>Hours: 24/7</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h4 className="text-lg font-medium text-amber-800 mb-2">Front Desk</h4>
                      <p>Email: gm@gulmohargrandhotel.com</p>
                      <p>Phone: +91-062305 62314</p>
                      <p>Hours: 24/7</p>
                    </div>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-lg mt-6">
                    <h4 className="text-lg font-medium text-amber-800 mb-2">Hotel Address</h4>
                    <p><b>Gulmohar Luxe Hotels Pvt. Ltd.</b><br /><a 
  href="https://devbhoomigroup.in" 
  target="_blank" 
  rel="noopener noreferrer" 
  className=" hover:underline"
>
  Dev Bhoomi Group Companies
</a>
,<br />Admin Block at The Gulmohar Grand,<br />8 mile stone, Una Chandigarh<br />Highway Behdala, Una,<br />Himachal Pradesh 174306</p>
                  </div>
                </SectionCard>
              )}
            </div>
          </div>

          {/* Footer Agreement */}
          <div className="mt-12 text-center" data-aos="fade-up">
            <p className="text-gray-600">
              By making a reservation or using our services, you agree to these terms and conditions.
            </p>

            <p className="text-gray-500 mt-2 text-sm">
              Last updated: {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
            </p>          </div>
        </div>
      </div>

      {/* Optional sticky bottom CTA for mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-inner text-center text-sm text-gray-700 md:hidden z-20">
        By continuing, you agree to our <span className="font-medium text-amber-700">Terms</span>
      </div>
    </div>
  );
};

export default TermsAndConditionsCardLayout;
